import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { integrationsAPI, okrSourcesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { Plug, Plus, Slack, Send, ScrollText, Trash2, Loader2, CheckCircle2, XCircle, Github, Target, RefreshCw, Copy, Link2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';

const PROVIDERS = [
  { key: 'slack', label: 'Slack', icon: Slack },
  { key: 'github', label: 'GitHub', icon: Github },
  { key: 'jira', label: 'Jira', icon: Plug },
  { key: 'linear', label: 'Linear', icon: Plug },
];
const DEV_PROVIDERS = ['github', 'jira', 'linear'];
const providerMeta = (k) => PROVIDERS.find((p) => p.key === k) || PROVIDERS[0];

const emptyForm = () => ({
  provider: 'slack', name: '', enabled: true, external_ref: '', base_url: '',
  webhook_url: '', signing_secret: '', webhook_secret: '', api_token: '', events: [],
});

const copy = (v) => { navigator.clipboard?.writeText(v || ''); Swal.fire({ icon: 'success', title: 'Copied', timer: 700, showConfirmButton: false }); };

const IntegrationsManagement = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);
  const webhookBase = (typeof window !== 'undefined' ? window.location.origin : '');

  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [events, setEvents] = useState([]);
  const [sources, setSources] = useState([]);
  const [okrs, setOkrs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [logsFor, setLogsFor] = useState(null);
  const [logs, setLogs] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({ key_result_id: '', connection_id: '', repo: '', project: '', team: '', label: '', event: '', increment: 1 });

  const load = async () => {
    setLoading(true);
    try {
      const [c, e, s] = await Promise.all([
        integrationsAPI.listConnections(),
        integrationsAPI.listEvents().catch(() => ({ data: [] })),
        okrSourcesAPI.list().catch(() => ({ data: [] })),
      ]);
      setConnections(c.data || []);
      setEvents(e.data || []);
      setSources(s.data || []);
    } catch (err) {
      if ([402, 403].includes(err?.response?.status)) setConnections([]);
      else Swal.fire('Could not load integrations', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleEvent = (key) => setForm((p) => ({ ...p, events: p.events.includes(key) ? p.events.filter((x) => x !== key) : [...p.events, key] }));
  const isDev = DEV_PROVIDERS.includes(form.provider);

  const openNew = () => { setEditingId(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ ...emptyForm(), provider: c.provider, name: c.name, enabled: c.enabled,
      external_ref: c.external_ref || '', base_url: (c.config || {}).base_url || '', events: c.events || [] });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return Swal.fire('Name required', 'Give this connection a name.', 'warning');
    const secrets = {};
    ['webhook_url', 'signing_secret', 'webhook_secret', 'api_token'].forEach((k) => { if (form[k]) secrets[k] = form[k]; });
    const config = {};
    if (form.provider === 'jira' && form.base_url) config.base_url = form.base_url;
    const payload = {
      provider: form.provider, name: form.name.trim(), enabled: form.enabled,
      external_ref: form.external_ref || null, events: form.events,
      ...(Object.keys(config).length ? { config } : {}),
      ...(Object.keys(secrets).length ? { secrets } : {}),
    };
    setSaving(true);
    try {
      if (editingId) await integrationsAPI.updateConnection(editingId, payload);
      else await integrationsAPI.createConnection(payload);
      setDialogOpen(false); await load();
      Swal.fire({ icon: 'success', title: editingId ? 'Connection updated' : 'Connection added', timer: 1300, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally { setSaving(false); }
  };

  const testConn = async (c) => {
    try { const r = await integrationsAPI.testConnection(c.id); Swal.fire(r.data.ok ? 'Sent ✅' : 'Delivery failed', r.data.detail || '', r.data.ok ? 'success' : 'error'); }
    catch (err) { Swal.fire('Test failed', err?.response?.data?.detail || '', 'error'); }
  };
  const removeConn = async (c) => {
    const ok = await Swal.fire({ icon: 'warning', title: 'Delete connection?', text: c.name, showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b' });
    if (!ok.isConfirmed) return;
    try { await integrationsAPI.deleteConnection(c.id); await load(); } catch (err) { Swal.fire('Delete failed', err?.response?.data?.detail || '', 'error'); }
  };
  const openLogs = async (c) => { setLogsFor(c); try { setLogs((await integrationsAPI.logs(c.id)).data || []); } catch { setLogs([]); } };
  const webhookUrl = (c) => `${webhookBase}/api/integrations/webhook/${(c.config || {}).inbound_token || ''}`;

  // ---- OKR auto-progress linking ----
  const devConnections = connections.filter((c) => DEV_PROVIDERS.includes(c.provider));
  const krOptions = useMemo(() => {
    const out = [];
    okrs.forEach((o) => (o.key_results || []).forEach((kr) => out.push({ id: kr.id, label: `${o.title} › ${kr.title}` })));
    return out;
  }, [okrs]);
  const linkedConn = devConnections.find((c) => String(c.id) === String(linkForm.connection_id));

  const openLink = async () => {
    setLinkForm({ key_result_id: '', connection_id: '', repo: '', project: '', team: '', label: '', event: '', increment: 1 });
    try { setOkrs((await okrSourcesAPI.listOkrs()).data || []); } catch { setOkrs([]); }
    setLinkOpen(true);
  };
  const submitLink = async () => {
    if (!linkForm.key_result_id || !linkForm.connection_id) return Swal.fire('Pick a key result and connection', '', 'warning');
    const match = {};
    ['repo', 'project', 'team', 'label', 'event'].forEach((k) => { if (linkForm[k]) match[k] = linkForm[k]; });
    try {
      await okrSourcesAPI.create({ key_result_id: Number(linkForm.key_result_id), connection_id: Number(linkForm.connection_id), match, increment: Number(linkForm.increment) || 1 });
      setLinkOpen(false); await load();
      Swal.fire({ icon: 'success', title: 'Key result linked', timer: 1300, showConfirmButton: false });
    } catch (err) { Swal.fire('Could not link', err?.response?.data?.detail || '', 'error'); }
  };
  const syncSource = async (s) => {
    try { const r = await okrSourcesAPI.sync(s.id); await load(); Swal.fire('Synced', `Current value: ${r.data.current_value}`, 'success'); }
    catch (err) { Swal.fire('Sync failed', err?.response?.data?.detail || '', 'error'); }
  };
  const unlink = async (s) => {
    const ok = await Swal.fire({ icon: 'warning', title: 'Unlink source?', text: s.key_result_title, showCancelButton: true, confirmButtonText: 'Unlink', confirmButtonColor: '#b5456b' });
    if (!ok.isConfirmed) return;
    try { await okrSourcesAPI.remove(s.id); await load(); } catch (err) { Swal.fire('Failed', err?.response?.data?.detail || '', 'error'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Plug className="h-6 w-6 text-indigo-600" />
          <div>
            <h2 className="text-2xl font-semibold">Integrations</h2>
            <p className="text-sm text-muted-foreground">Slack notifications &amp; slash commands · GitHub / Jira / Linear → OKR auto-progress.</p>
          </div>
        </div>
        {canManage && <Button onClick={openNew}><Plus className="h-4 w-4 mr-1" /> Add connection</Button>}
      </div>

      {/* Connections */}
      <Card>
        <CardHeader><CardTitle className="text-base">Connections</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
          ) : connections.length === 0 ? (
            <EmptyState icon={Plug} title="No integrations yet" description={canManage ? 'Connect Slack, GitHub, Jira or Linear.' : 'No integrations configured.'} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Provider</TableHead><TableHead>Name</TableHead><TableHead>Webhook / Events</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {connections.map((c) => {
                    const M = providerMeta(c.provider); const Icon = M.icon; const dev = DEV_PROVIDERS.includes(c.provider);
                    return (
                      <TableRow key={c.id}>
                        <TableCell><span className="inline-flex items-center gap-1.5 font-medium"><Icon className="h-4 w-4" /> {M.label}</span></TableCell>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>
                          {dev ? (
                            <button className="text-xs text-indigo-600 inline-flex items-center gap-1" onClick={() => copy(webhookUrl(c))} title="Copy inbound webhook URL"><Copy className="h-3 w-3" /> webhook URL</button>
                          ) : (c.events || []).length ? <Badge variant="secondary">{c.events.length} events</Badge> : <span className="text-muted-foreground text-xs">all events</span>}
                        </TableCell>
                        <TableCell>{c.enabled ? <Badge className="bg-emerald-600">Enabled</Badge> : <Badge variant="secondary">Disabled</Badge>}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {canManage && c.provider === 'slack' && <Button variant="ghost" size="icon" onClick={() => testConn(c)} title="Send test"><Send className="h-4 w-4" /></Button>}
                          <Button variant="ghost" size="icon" onClick={() => openLogs(c)} title="Delivery log"><ScrollText className="h-4 w-4" /></Button>
                          {canManage && <Button variant="ghost" size="sm" onClick={() => openEdit(c)}>Edit</Button>}
                          {canManage && <Button variant="ghost" size="icon" onClick={() => removeConn(c)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* OKR auto-progress */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Target className="h-4 w-4" /> OKR auto-progress</CardTitle>
            <CardDescription>Advance key results automatically from merged PRs / closed issues.</CardDescription>
          </div>
          {canManage && <Button variant="outline" onClick={openLink} disabled={devConnections.length === 0}><Link2 className="h-4 w-4 mr-1" /> Link key result</Button>}
        </CardHeader>
        <CardContent>
          {sources.length === 0 ? (
            <EmptyState icon={Target} title="No linked key results" description={devConnections.length === 0 ? 'Add a GitHub / Jira / Linear connection first.' : 'Link a key result to a repo, project or team.'} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Key result</TableHead><TableHead>Source</TableHead><TableHead>Match</TableHead><TableHead>Progress</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {sources.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell><div className="font-medium">{s.key_result_title}</div><div className="text-xs text-muted-foreground">{s.objective_title}</div></TableCell>
                      <TableCell><Badge variant="secondary">{s.provider}</Badge> {s.connection_name}</TableCell>
                      <TableCell className="text-xs">{Object.entries(s.match || {}).map(([k, v]) => `${k}:${v}`).join(', ') || 'all'}</TableCell>
                      <TableCell>{s.current_value ?? 0} / {s.target_value ?? 0}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {canManage && <Button variant="ghost" size="icon" onClick={() => syncSource(s)} title="Sync now"><RefreshCw className="h-4 w-4" /></Button>}
                        {canManage && <Button variant="ghost" size="icon" onClick={() => unlink(s)} title="Unlink"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / edit connection dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editingId ? 'Edit connection' : 'Add connection'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!editingId && (
              <div className="space-y-1">
                <Label>Provider</Label>
                <Select value={form.provider} onValueChange={(v) => set('provider', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1"><Label>Name</Label><Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Engineering" /></div>

            {form.provider === 'slack' && (<>
              <div className="space-y-1"><Label>Incoming webhook URL {editingId && <span className="text-xs text-muted-foreground">(blank = keep)</span>}</Label><Input type="password" value={form.webhook_url} onChange={(e) => set('webhook_url', e.target.value)} placeholder="https://hooks.slack.com/services/…" /></div>
              <div className="space-y-1"><Label>Signing secret <span className="text-xs text-muted-foreground">(slash commands)</span></Label><Input type="password" value={form.signing_secret} onChange={(e) => set('signing_secret', e.target.value)} /></div>
              <div className="space-y-1"><Label>Slack Team ID</Label><Input value={form.external_ref} onChange={(e) => set('external_ref', e.target.value)} placeholder="T01234567" /></div>
              <div className="space-y-2">
                <Label>Notify on events <span className="text-xs text-muted-foreground">(none = all)</span></Label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto rounded-md border p-2">
                  {events.map((ev) => <label key={ev.key} className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={form.events.includes(ev.key)} onCheckedChange={() => toggleEvent(ev.key)} /> {ev.label}</label>)}
                </div>
              </div>
            </>)}

            {isDev && (<>
              {form.provider === 'github' && <div className="space-y-1"><Label>Webhook secret {editingId && <span className="text-xs text-muted-foreground">(blank = keep)</span>}</Label><Input type="password" value={form.webhook_secret} onChange={(e) => set('webhook_secret', e.target.value)} placeholder="signs incoming webhooks" /></div>}
              {form.provider === 'jira' && <div className="space-y-1"><Label>Jira base URL</Label><Input value={form.base_url} onChange={(e) => set('base_url', e.target.value)} placeholder="https://acme.atlassian.net" /></div>}
              <div className="space-y-1"><Label>API token <span className="text-xs text-muted-foreground">(for manual sync / poll)</span></Label><Input type="password" value={form.api_token} onChange={(e) => set('api_token', e.target.value)} /></div>
              <p className="text-xs text-muted-foreground">After saving, copy the connection's <b>webhook URL</b> (from the table) into your {providerMeta(form.provider).label} webhook settings.</p>
            </>)}

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div><div className="font-medium">Enabled</div></div>
              <Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {editingId ? 'Save' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link KR dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Link a key result</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Key result</Label>
              <Select value={linkForm.key_result_id} onValueChange={(v) => setLinkForm((p) => ({ ...p, key_result_id: v }))}>
                <SelectTrigger><SelectValue placeholder={krOptions.length ? 'Select a key result' : 'No OKRs found'} /></SelectTrigger>
                <SelectContent>{krOptions.map((k) => <SelectItem key={k.id} value={String(k.id)}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Source connection</Label>
              <Select value={linkForm.connection_id} onValueChange={(v) => setLinkForm((p) => ({ ...p, connection_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select a GitHub/Jira/Linear connection" /></SelectTrigger>
                <SelectContent>{devConnections.map((c) => <SelectItem key={c.id} value={String(c.id)}>{providerMeta(c.provider).label} · {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {linkedConn?.provider === 'github' && (<>
              <div className="space-y-1"><Label>Repository</Label><Input value={linkForm.repo} onChange={(e) => setLinkForm((p) => ({ ...p, repo: e.target.value }))} placeholder="acme/api" /></div>
              <div className="space-y-1"><Label>Count</Label>
                <Select value={linkForm.event || 'pr_merged'} onValueChange={(v) => setLinkForm((p) => ({ ...p, event: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="pr_merged">Merged PRs</SelectItem><SelectItem value="issue_closed">Closed issues</SelectItem></SelectContent>
                </Select>
              </div>
            </>)}
            {linkedConn?.provider === 'jira' && <div className="space-y-1"><Label>Project key</Label><Input value={linkForm.project} onChange={(e) => setLinkForm((p) => ({ ...p, project: e.target.value }))} placeholder="ENG" /></div>}
            {linkedConn?.provider === 'linear' && <div className="space-y-1"><Label>Team key</Label><Input value={linkForm.team} onChange={(e) => setLinkForm((p) => ({ ...p, team: e.target.value }))} placeholder="ENG" /></div>}
            {linkedConn && (<>
              <div className="space-y-1"><Label>Label filter <span className="text-xs text-muted-foreground">(optional)</span></Label><Input value={linkForm.label} onChange={(e) => setLinkForm((p) => ({ ...p, label: e.target.value }))} placeholder="backend" /></div>
              <div className="space-y-1"><Label>Increment per event</Label><Input type="number" value={linkForm.increment} onChange={(e) => setLinkForm((p) => ({ ...p, increment: e.target.value }))} /></div>
            </>)}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>Cancel</Button>
            <Button onClick={submitLink}>Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery log dialog */}
      <Dialog open={!!logsFor} onOpenChange={(o) => !o && setLogsFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Delivery log · {logsFor?.name}</DialogTitle></DialogHeader>
          {logs.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No deliveries yet.</p> : (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Dir</TableHead><TableHead>Event</TableHead><TableHead>Status</TableHead><TableHead>Detail</TableHead></TableRow></TableHeader>
                <TableBody>
                  {logs.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="whitespace-nowrap text-xs">{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</TableCell>
                      <TableCell className="text-xs">{l.direction}</TableCell>
                      <TableCell className="text-xs">{l.event_type}</TableCell>
                      <TableCell>{l.status === 'success' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : l.status === 'failed' ? <XCircle className="h-4 w-4 text-rose-600" /> : <Badge variant="secondary">{l.status}</Badge>}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{l.detail}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationsManagement;
